import { useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { sendAnalyticsInteractionEvent } from "src/analytics/sendAnalyticsEvent";
import { CloseButton } from "src/auth/components/CloseButton/CloseButton";
import type { AutocompletePlace } from "../../api/v1.6/autocomplete";
import { AutocompleteInput } from "../../components/AutocompleteInput/AutocompleteInput";
import { AutocompleteList } from "../../components/AutocompleteList/AutocompleteList";
import { Portal } from "../../components/Portal/Portal";
import useAutocomplete from "../../utils/hooks/useAutocomplete";
import { useTypedLocation } from "../../utils/hooks/useTypedLocation";
import messages from "./AutocompleteScreen.messages";

export type AutocompleteKind = "origin" | "destination" | number;

type Props = {
  onSelectOption: (place: AutocompletePlace) => void;
  initialQuery?: string;
};

export function AutocompleteScreen(props: Props) {
  const navigate = useNavigate();
  const location = useTypedLocation();
  const autocompleteState = location.state?.autocomplete!;
  const { results, query, changeQuery } = useAutocomplete(
    props.initialQuery,
    Boolean(props.initialQuery),
  );
  const intl = useIntl();

  const handleAutocompleteSelect = (place: AutocompletePlace) => {
    sendAnalyticsInteractionEvent({
      category: "Autocomplete",
      action: "Click:AutocompleteOption",
    });
    props.onSelectOption(place);
  };

  const handleCloseClick = () => {
    sendAnalyticsInteractionEvent({
      category: "Autocomplete",
      action: "Click:Close",
    });
    navigate(-1);
  };

  return (
    <Portal>
      <div className="fixed top-0 z-10001 h-full w-full overflow-y-scroll bg-surface p-4">
        <div className="mb-2 flex items-center justify-between">
          <AutocompleteInput
            className="flex-shrink-0 flex-grow basis-auto"
            type={autocompleteState}
            value={query}
            onChange={changeQuery}
            // We can disable this autoFocus rule because we only ever have one instance
            // in the DOM at any one time.
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
          <CloseButton
            className="ml-4 align-middle"
            onClick={handleCloseClick}
            aria-label={intl.formatMessage(messages.close)}
          />
        </div>
        <AutocompleteList
          isInputFocused={true}
          results={results}
          onSelectPlace={handleAutocompleteSelect}
        />
      </div>
    </Portal>
  );
}
